import { useT } from '@eturi/react-i18next'
import cls from 'classnames'
import type { InputHTMLAttributes, Ref } from 'react'
import { forwardRef } from 'react'
import type { FieldProps, FieldRenderProps } from 'react-final-form'
import { Field } from 'react-final-form'
import { TRIM_INPUT } from '../../common'
import { scrollOnFocus } from '../../util/scrollOnFocus'
import { FieldError, hasFieldError } from '../../widgets'

type InputFieldProps = InputHTMLAttributes<HTMLInputElement> & { readonly 'data-testid'?: string }

export type OPInputFieldProps = InputFieldProps & {
	readonly field?: FieldProps<string, any, HTMLInputElement>
	readonly fieldClassName?: string
	readonly fieldName?: string
}

export const createInputField = (
	fieldProps: FieldProps<string, any, HTMLInputElement>,
	defaultProps: InputFieldProps = {},
	displayName = 'OPInput',
) => {
	const OPInput = forwardRef(
		({ field, fieldClassName, fieldName, ...p }: OPInputFieldProps, ref: Ref<HTMLInputElement>) => {
			const [t] = useT()
			const mergedFieldProps = {
				...TRIM_INPUT,
				...fieldProps,
				...field,
				name: fieldName || field?.name || fieldProps.name,
			}
			const mergedInputProps = { ...defaultProps, ...p }
			const placeholder =
				p.placeholder ?? ((defaultProps.placeholder ? t(defaultProps.placeholder) : '') || '')

			/**
			 * NOTE: we have to normalize 'value' if it starts undefined, or React will consider the
			 *  input uncontrolled and throw an error when it changes. This is a regression in
			 *  react-final-form.
			 *  See https://github.com/final-form/react-final-form/issues/529
			 */
			return (
				<Field {...mergedFieldProps}>
					{({ input, meta }: FieldRenderProps<string, HTMLInputElement>) => (
						<div className={cls('field', { error: hasFieldError(meta) }, fieldClassName)}>
							<input
								ref={ref}
								{...input}
								{...mergedInputProps}
								className={cls('input', mergedInputProps.className)}
								onBlur={(ev) => {
									input.onBlur(ev)
									mergedInputProps.onBlur?.(ev)
								}}
								onFocus={(ev) => {
									scrollOnFocus(ev)
									input.onFocus(ev)
									mergedInputProps.onFocus?.(ev)
								}}
								placeholder={placeholder}
								type={mergedInputProps.type || 'text'}
								value={input.value || ''}
							/>
							<FieldError meta={meta} />
						</div>
					)}
				</Field>
			)
		},
	)

	OPInput.displayName = displayName

	return OPInput
}
